// 利用规约（consent）相关后端接口
// 后端实现：felo-mygpt consent 模块（user_consent 表 + robot_consent_content 表）
// - GET/POST /users/me/consents/{type}（realtime 旧路径保留，内部同一实现）
// - GET/PUT /robots/{bot_id}/consent-content/{type}（version = sha256(content)[:16]）

import { Http } from '@mygpt/app/http';
import { getRealtimeConsent, saveRealtimeConsent } from '@mygpt/app/apis';

import type { ConsentType } from '../types';

/** 用户对某类规约的同意记录 */
export interface UserConsentRecord {
  consented: boolean;
  consented_at?: string | null;
  version?: string | null;
}

/**
 * 获取当前登录用户对指定类型规约的同意记录
 * - realtime：走既有接口 GET /users/me/realtime-consent（保持现有行为不变）
 * - chat：后端未就绪，预设路径 GET /users/me/consents/{type}
 * 返回 null 表示无记录 / 接口未就绪（调用方应回退到 localStorage）
 */
export const getUserConsent = async (type: ConsentType): Promise<UserConsentRecord | null> => {
  if (type === 'realtime') {
    const { data } = await getRealtimeConsent();
    return data ?? null;
  }
  const { get } = Http.use();
  const { data } = await get<UserConsentRecord>(`/users/me/consents/${type}`);
  return data ?? null;
};

export interface SaveUserConsentParams {
  version: string;
}

/**
 * 保存当前登录用户对指定类型规约的同意记录（best-effort）
 * - realtime：走既有接口 POST /users/me/realtime-consent（保持现有行为不变）
 * - chat：后端未就绪，预设路径 POST /users/me/consents/{type}
 */
export const saveUserConsent = async (type: ConsentType, params: SaveUserConsentParams): Promise<void> => {
  if (type === 'realtime') {
    await saveRealtimeConsent(params);
    return;
  }
  const { post } = Http.use();
  await post(`/users/me/consents/${type}`, params);
};

/** bot 级自定义规约内容 */
export interface RobotConsentContent {
  /** Markdown 正文；null 表示未设置自定义规约（使用内置模版兜底） */
  content: string | null;
  /** 内容版本；null 表示未设置。内容变更即视为新 version */
  version: string | null;
  updated_at?: string | null;
}

export interface GetRobotConsentContentParams {
  botId: string;
  /** chat 与 realtime 的自定义规约各自独立一份 */
  consentType: ConsentType;
}

/**
 * 获取 bot 级自定义规约内容（chat / realtime 各自独立，bot 级）
 * 返回 null 表示未设置（调用方回退到内置模版）
 */
export const getRobotConsentContent = async (
  params: GetRobotConsentContentParams
): Promise<RobotConsentContent | null> => {
  // 未设置时后端返回 200 + 三字段 null，统一折叠为 null（调用方回退内置模版）
  const { get } = Http.use();
  const { data } = await get<RobotConsentContent>(`/robots/${params.botId}/consent-content/${params.consentType}`);
  return data?.content ? data : null;
};

export interface SaveRobotConsentContentParams {
  botId: string;
  /** chat 与 realtime 的自定义规约各自独立一份 */
  consentType: ConsentType;
  /** Markdown 正文；空字符串表示清除自定义规约（回退内置模版） */
  content: string;
}

/**
 * 保存 bot 级自定义规约内容
 * 版本策略：内容变更即视为新 version。当前 mock 用保存时间戳；
 * 等后端契约确定用内容 hash 还是时间戳（两者对前端逻辑等价：同意记录按版本字符串精确比对）
 */
export const saveRobotConsentContent = async (params: SaveRobotConsentContentParams): Promise<RobotConsentContent> => {
  // 空 content = 清除自定义规约（后端删除记录并返回全 null）；version 由后端按内容 sha256 生成
  const { put } = Http.use();
  const { data } = await put<RobotConsentContent>(`/robots/${params.botId}/consent-content/${params.consentType}`, {
    content: params.content,
  });
  return data;
};
